import { Component, effect, inject, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { HeaderComponent } from '../../atoms/header/header.component';
import { LayerDropdownComponent } from '../../atoms/layer-dropdown/layer-dropdown.component';
import { NgForOf, NgIf, SlicePipe } from '@angular/common';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { MessageService } from 'primeng/api';
import { getState } from '@ngrx/signals';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  Feature,
  GeoData,
  supportedProjections,
} from '../../../models/geo-data';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { FeatureFormComponent } from '../../molecules/feature-form/feature-form.component';
import { FileBeforeUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { GeoDataService } from '../../../services/geo-data/geo-data.service';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from 'primeng/divider';
import { JoinPipe } from '../../../pipes/join.pipe';
import { BasicMapComponent } from '../../molecules/basic-map/basic-map.component';
import { DataLayer } from '../../../models/data-layer';
import { DataLayerType } from '../../../models/data-layer-type';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-import-export-geodata',
  standalone: true,
  imports: [
    ButtonModule,
    HeaderComponent,
    LayerDropdownComponent,
    NgIf,
    TriStateCheckboxModule,
    TypeDropdownComponent,
    ReactiveFormsModule,
    NgForOf,
    FormFieldWrapperComponent,
    InputTextModule,
    FeatureFormComponent,
    FileUploadModule,
    DropdownModule,
    FormsModule,
    ToastModule,
    DividerModule,
    JoinPipe,
    BasicMapComponent,
    AlertComponent,
    RouterLink,
    TooltipModule,
    SlicePipe,
  ],
  templateUrl: './import-export-geodata.component.html',
  styleUrl: './import-export-geodata.component.scss',
})
export class ImportExportGeodataComponent {
  protected readonly supportedProjections = supportedProjections;
  readonly globalStore = inject(GlobalsStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly geoDataStore = inject(GeoDataStore);

  @ViewChild('importFeatureForm') importFeatureForm?: FeatureFormComponent;

  acceptedFileExtensions = '.geojson, .zip, .kml, .gml, .xml, .osm, .pbf';
  selectedLayer: DataLayer | null = null;
  selectedProjection = 'EPSG:4326';
  selectedType: DataLayerType | null = null;
  selectedGeoData: GeoData | null = null;
  uploadData: any = undefined;
  dataToImport: any = undefined;
  importFeatureProperties: string[] = [];
  importFeatureValues: Record<string, string[]> = {};
  initialMapping: { [key: string]: string } = {
    key: '',
    name: '',
    group: '',
    description: '',
    address: '',
    contact: '',
    information: '',
  };
  mappedProperties: { [key: string]: string } = { ...this.initialMapping };

  geoProperties = [
    {
      key: 'key',
      label: 'Schlüssel',
      required: true,
    },
    {
      key: 'name',
      label: 'Name',
      required: true,
    },
    {
      key: 'group',
      label: 'Gruppe',
      required: false,
    },
  ];

  poiGeoProperties = this.geoProperties.concat(
    {
      key: 'description',
      label: 'Beschreibung',
      required: false,
    },
    {
      key: 'address',
      label: 'Adresse',
      required: false,
    },
    {
      key: 'contact',
      label: 'Kontakt',
      required: false,
    },
    {
      key: 'information',
      label: 'Information',
      required: false,
    }
  );

  /**
   * Determines the geographic data type based on the selected type.
   *
   * @return {'Point' | 'Polygon'} Returns 'Point' if the selected type includes an icon, otherwise returns 'Polygon'. Defaults to 'Polygon' if no type is selected.
   */
  get typeOfGeoData(): 'Point' | 'Polygon' {
    if (!this.selectedType) return 'Polygon';
    return this.selectedType.icon ? 'Point' : 'Polygon';
  }

  constructor(
    protected geoDataService: GeoDataService,
    private messageService: MessageService
  ) {
    effect(() => {
      const globalState = getState(this.globalStore);

      this.selectedType = globalState.selectedType;
      this.selectedGeoData = globalState.selectedGeoData;
      this.selectedLayer = globalState.selectedLayer
        ? globalState.selectedLayer
        : null;
    });
  }

  /**
   * adds projection as parameter
   * @param event
   */
  onBeforeSend(event: FileBeforeUploadEvent): void {
    const additionalParams: { [key: string]: string } = {
      projection: this.selectedProjection,
    };

    // Append additional parameters to the form data
    Object.keys(additionalParams).forEach(key => {
      event.formData.append(key, additionalParams[key]);
    });
  }

  /**
   * Handles the upload event, processes the uploaded data, retrieves grouped properties,
   * and displays a success message upon completion.
   *
   * @param {any} event - The event object containing details of the file upload,
   *                      including the uploaded file data in the body.
   * @return {void} - Does not return any value.
   */
  onUpload(event: any): void {
    this.uploadData = event.originalEvent.body;
    this.dataToImport = JSON.parse(JSON.stringify(event.originalEvent.body)); // deep-copy
    this.fetchGroupedPropertiesFromImport();
    this.messageService.add({
      severity: 'info',
      summary: 'Upload erfolgreich!',
    });
  }

  /**
   * Processes and groups properties from the features in the uploaded data.
   * Iterates over the properties of each feature to aggregate unique values for each property key.
   * The grouped property values are stored and converted into arrays for further processing.
   *
   * @return {void} This method does not return a value, but it updates `importFeatureProperties` and `importFeatureValues`
   *                with the extracted property keys and their corresponding unique values.
   */
  private fetchGroupedPropertiesFromImport(): void {
    const propertyGroups: Record<string, Set<any>> = {};
    this.uploadData.features.forEach((feature: Feature) => {
      Object.keys(feature.properties).forEach(key => {
        if (!propertyGroups[key]) {
          propertyGroups[key] = new Set();
        }
        propertyGroups[key].add(feature.properties[key]);
      });
    });

    this.importFeatureProperties = Object.keys(propertyGroups);
    // Convert the sets to arrays
    this.importFeatureProperties.forEach(key => {
      this.importFeatureValues[key] = Array.from(propertyGroups[key]);
    });
  }

  /**
   * Updates the model by mapping the provided key to the value from the event object
   * and triggers the generation of geo-data for import.
   *
   * @param {string} key - The key used to identify the property to update in the model.
   * @param {any} event - The event object containing the updated value.
   * @return {void} - Does not return a value.
   */
  updateModel(key: string, event: any): void {
    this.mappedProperties[key] = event.value;
    this.generateGeoDataToImport();
  }

  /**
   * Generates and maps geospatial data by updating the properties of features in the data set with values from the uploaded data.
   *
   * It iterates through each feature in the data set and maps specific properties from the uploaded data based on the provided mapping keys. Additionally, if an import feature form exists, it will trigger rendering of the form.
   *
   * @return {void} This method does not return any value.
   */
  generateGeoDataToImport(): void {
    this.dataToImport.features.map((feature: Feature, idx: number) => {
      feature.properties = {
        key: this.uploadData.features[idx].properties[
          this.mappedProperties['key']
        ],
        name: this.uploadData.features[idx].properties[
          this.mappedProperties['name']
        ],
        group:
          this.uploadData.features[idx].properties[
            this.mappedProperties['group']
          ],
        information:
          this.uploadData.features[idx].properties[
            this.mappedProperties['information']
          ],
        contact:
          this.uploadData.features[idx].properties[
            this.mappedProperties['contact']
          ],
        description:
          this.uploadData.features[idx].properties[
            this.mappedProperties['description']
          ],
        address:
          this.uploadData.features[idx].properties[
            this.mappedProperties['address']
          ],
      };
    });
    if (this.importFeatureForm) {
      this.importFeatureForm.renderForm();
    }
  }

  cancel() {
    this.clear();
  }

  clear() {
    this.uploadData = undefined;
    this.dataToImport = undefined;
    this.mappedProperties = { ...this.initialMapping };
  }

  /**
   * Initiates the download of a GeoJSON file based on the current selected type and geo data.
   * The filename is determined by the name of the selected type with a .geojson extension.
   *
   * @return {void} No return value.
   */
  downloadGeoJson(): void {
    if (this.selectedType && this.selectedGeoData) {
      const filename = `${this.selectedType.name}.geojson`;
      this.geoDataStore.downloadSelected(filename);
    }
  }
}
